import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  Target,
  TrendingUp,
  Sparkles,
  ArrowLeft
} from 'lucide-react';

/**
 * Assessments Coming Soon Page
 * 
 * A polished preview page for the upcoming Assessments module.
 * Shows for all users when the module is not yet enabled.
 */
export default function AssessmentsComingSoonPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: ClipboardCheck,
      title: 'Universal Skill Screeners',
      description: 'Screen all students efficiently to identify those who may need additional support.'
    },
    {
      icon: Target,
      title: 'Targeted Follow-Up Probes',
      description: 'Dive deeper with skill-specific assessments to pinpoint exact areas of need.'
    },
    {
      icon: Sparkles,
      title: 'Assessment-to-Intervention Recommendations',
      description: 'Get intelligent suggestions for interventions based on assessment results.'
    },
    {
      icon: TrendingUp,
      title: 'Built-in Baseline Support',
      description: 'Automatically establish baselines and connect results to progress monitoring.'
    }
  ];

  return (
    <div className="page-container" data-testid="assessments-coming-soon-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/students')}
          className="flex items-center gap-2 text-sm text-[#595959] hover:text-[#2C4C3B] mb-4 transition-colors"
          data-testid="back-to-students-btn"
        >
          <ArrowLeft size={16} />
          Back to Students
        </button>
      </div>

      {/* Hero Section */}
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2C4C3B]/10 rounded-full text-[#2C4C3B] text-sm font-medium mb-6">
          <Sparkles size={16} />
          Coming Soon
        </div>
        
        <h1 className="text-4xl font-semibold text-[#1C1C1C] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Assessments
        </h1>
        
        <p className="text-lg text-[#595959] mb-6">
          Coming Soon to Frame Interventions
        </p>
        
        <p className="text-[#595959] leading-relaxed max-w-2xl mx-auto">
          We're building an integrated assessment layer to help schools screen students, 
          identify skill needs, and connect results directly to interventions and progress monitoring. 
          This module will seamlessly integrate with your existing intervention workflows.
        </p>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-12">
        {features.map((feature, index) => (
          <div
            key={index}
            className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg hover:border-[#2C4C3B]/30 transition-all duration-200"
          >
            <div className="w-12 h-12 bg-[#2C4C3B]/10 rounded-xl flex items-center justify-center mb-4">
              <feature.icon size={24} className="text-[#2C4C3B]" />
            </div>
            <h3 className="text-lg font-semibold text-[#1C1C1C] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {feature.title}
            </h3>
            <p className="text-sm text-[#595959] leading-relaxed">
              {feature.description}
            </p>
          </div>
        ))}
      </div>

      {/* What's Coming Section */}
      <div className="bg-[#F3F4F1] rounded-2xl p-8 max-w-4xl mx-auto mb-12">
        <h2 className="text-xl font-semibold text-[#1C1C1C] mb-6 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
          What to Expect
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-[#2C4C3B] mb-2">Phase 1</div>
            <div className="text-sm text-[#595959]">
              Screener templates for<br />Reading & Math
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#2C4C3B] mb-2">Phase 2</div>
            <div className="text-sm text-[#595959]">
              Skill probes &<br />follow-up assessments
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-[#2C4C3B] mb-2">Phase 3</div>
            <div className="text-sm text-[#595959]">
              Automated intervention<br />recommendations
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center max-w-xl mx-auto">
        {/* Pilot Interest - Informational Only */}
        <div className="bg-[#F3F4F1] rounded-xl px-6 py-4 mb-6">
          <p className="text-sm text-[#595959]">
            <span className="font-medium text-[#1C1C1C]">Interested in early access?</span>
            {' '}Contact your school administrator to request pilot enrollment.
          </p>
        </div>
        
        <div>
          <button
            onClick={() => navigate('/students')}
            className="btn btn-primary px-6 py-2"
            data-testid="return-to-interventions-btn"
          >
            Return to Interventions
          </button>
        </div>
        
        <p className="text-xs text-[#A3A3A3] mt-6">
          Questions? Contact your administrator or reach out to the FrameEd support team.
        </p>
      </div>
    </div>
  );
}
